import React, { useCallback } from 'react';
import { useResumeContext } from '../../context/ResumeContext';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * ExpandableSection Component
 * 
 * Creates a toggleable section with header and expandable content.
 * Maintains expanded state in the global ResumeContext.
 * Includes smooth animations for expanding/collapsing.
 * 
 * @param {string} id - Unique identifier for the section
 * @param {string} title - Section title to display in header
 * @param {React.ReactNode} children - Content to display when expanded
 * @param {boolean} initialExpanded - Whether section should start expanded
 * @param {string} iconColor - Color for the expansion indicator (blue, green, purple, etc.)
 */
const ExpandableSection = ({ 
  id, 
  title, 
  children, 
  initialExpanded = false,
  iconColor = 'blue'
}) => {
  const { expandedSections, toggleSection } = useResumeContext();
  
  // Get expanded state from context, defaulting to initialExpanded if not set
  const isExpanded = expandedSections[id] === undefined 
    ? initialExpanded 
    : expandedSections[id];
  
  /**
   * Toggle the expanded state of this section
   */
  const handleToggle = useCallback(() => {
    toggleSection(id);
  }, [id, toggleSection]);

  // Map color names to Tailwind classes
  const colorClasses = {
    blue: 'text-blue-500 hover:text-blue-700 hover:bg-blue-50',
    green: 'text-green-500 hover:text-green-700 hover:bg-green-50',
    purple: 'text-purple-500 hover:text-purple-700 hover:bg-purple-50',
    teal: 'text-teal-500 hover:text-teal-700 hover:bg-teal-50',
    orange: 'text-orange-500 hover:text-orange-700 hover:bg-orange-50'
  };
  
  const colorClass = colorClasses[iconColor] || colorClasses.blue;
  
  return (
    <div className="expandable-section mb-4 border rounded-lg shadow-sm overflow-hidden">
      <motion.div 
        className={`expandable-header flex justify-between items-center p-4 cursor-pointer ${isExpanded ? 'bg-slate-50' : 'bg-white'}`}
        onClick={handleToggle}
        role="button"
        aria-expanded={isExpanded}
        tabIndex={0}
        onKeyPress={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            handleToggle();
          }
        }}
        whileHover={{ backgroundColor: isExpanded ? '#F8FAFC' : '#F9FAFB' }}
        whileTap={{ scale: 0.98 }}
      >
        <h3 className="text-lg font-bold">{title}</h3>
        <motion.div 
          className={`w-8 h-8 rounded-full flex items-center justify-center ${colorClass}`}
          initial={false}
          animate={{ rotate: isExpanded ? 180 : 0 }}
          transition={{ duration: 0.3 }}
        >
          <motion.span 
            className="block"
            aria-hidden="true"
          >
            ▼
          </motion.span>
        </motion.div>
      </motion.div>
      
      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="p-4 pt-2 border-t">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ExpandableSection;
